import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEmbedContext } from '@/hooks/useEmbedContext';
import { useAuth } from '@/contexts/AuthContext';
import { Icon } from '@/components/Icon';

const NAV_ITEMS = [
  { to: '/ui-orchestrator', label: 'Projects', icon: 'folder_open', end: true },
  { to: '/ui-orchestrator/services', label: 'Services', icon: 'hub' },
  { to: '/ui-orchestrator/services', label: 'Analytics', icon: 'analytics' },
];

const SIDEBAR_WIDTH = 256;

export function Layout() {
  const { isEmbedded } = useEmbedContext();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');

  const showSidebar = true;

  return (
    <div style={{ fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      {/* Sidebar */}
      {showSidebar && (
        <aside
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            width: SIDEBAR_WIDTH,
            backgroundColor: '#eef2ff',
            borderRight: '1px solid rgba(226, 232, 240, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 32,
            padding: 24,
            zIndex: 40,
          }}
        >
          {/* Brand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 8px' }}>
            <div
              style={{
                width: 40,
                height: 40,
                background: 'linear-gradient(135deg, #4a40e0 0%, #9795ff 100%)',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <Icon name="deployed_code_filled" size={20} />
            </div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#312e81', lineHeight: 1.2, margin: 0 }}>
                UI Orchestrator
              </h2>
              <p
                style={{
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontSize: 10,
                  color: '#6366f1',
                  margin: 0,
                }}
              >
                v2.4.0-stable
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to + item.label}
                to={item.to}
                end={item.end}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  borderRadius: 12,
                  textDecoration: 'none',
                  transition: 'all 150ms ease',
                  backgroundColor: isActive ? '#ffffff' : 'transparent',
                  color: isActive ? '#4a40e0' : '#4338ca',
                  boxShadow: isActive ? '0 12px 40px rgba(74, 64, 224, 0.06)' : 'none',
                  transform: isActive ? 'translateX(4px)' : 'none',
                })}
              >
                <Icon name={item.icon} size={20} />
                <span
                  style={{
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontSize: 10,
                  }}
                >
                  {item.label}
                </span>
              </NavLink>
            ))}
          </nav>

          {/* New Project Button */}
          <button
            onClick={() => navigate('/ui-orchestrator/projects/new')}
            style={{
              background: 'linear-gradient(135deg, #4a40e0 0%, #9795ff 100%)',
              color: '#fff',
              fontWeight: 700,
              padding: '16px 0',
              borderRadius: 12,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(74, 64, 224, 0.25)',
              fontSize: 14,
              transition: 'transform 150ms ease',
            }}
          >
            <Icon name="add" size={20} />
            <span>New Project</span>
          </button>
        </aside>
      )}

      {/* Top Navigation */}
      <header
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          left: showSidebar ? SIDEBAR_WIDTH : 0,
          zIndex: 50,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.2)',
          boxShadow: '0 4px 20px rgba(44, 47, 48, 0.04)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            padding: '12px 32px',
          }}
        >
          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flex: 1 }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: 448 }}>
              <span
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#94a3b8',
                  display: 'flex',
                }}
              >
                <Icon name="search" size={18} />
              </span>
              <input
                type="text"
                placeholder="Search orchestrations..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: 'var(--color-surface-container-low, #eff1f2)',
                  border: 'none',
                  borderRadius: 4,
                  paddingLeft: 40,
                  paddingRight: 16,
                  paddingTop: 8,
                  paddingBottom: 8,
                  fontSize: 14,
                  outline: 'none',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          </div>

          {/* Right side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              style={{
                padding: 8,
                color: '#64748b',
                background: 'transparent',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="notifications" size={24} />
            </button>

            {!isEmbedded && (
              <>
                <div style={{ height: 32, width: 1, backgroundColor: '#e2e8f0', margin: '0 8px' }} />
                <button
                  onClick={signOut}
                  style={{
                    color: '#64748b',
                    fontSize: 14,
                    fontWeight: 500,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                  }}
                >
                  Sign out
                </button>
              </>
            )}

            {/* Avatar placeholder */}
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #4a40e0 0%, #9795ff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                border: '2px solid #fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              U
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        style={{
          marginLeft: showSidebar ? SIDEBAR_WIDTH : 0,
          paddingTop: 96,
          paddingBottom: 48,
          paddingLeft: 32,
          paddingRight: 32,
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </main>
    </div>
  );
}
